import { T, useT } from '@transifex/react';
import { Heading, HStack, Text, VStack } from '@vidahealth/vida-ui';
import { View } from 'react-native';

import CheckIcon from '@/assets/icons/check-icon.svg';
import WelcomeSsoUserImage from '@/assets/images/welcome-sso-user-image.svg';
import { BottomSheet, CtaButton } from '@/modules/ui';

type WelcomeBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  /** First name for the greeting (SSO). Falls back to a generic title. */
  greetingName?: string;
};

// Pre-account welcome drawer shared by the flows that greet the member before
// they start (SSO + PURL) — mirrors Sparta's single onboarding WelcomeDrawer.
export function WelcomeBottomSheet({
  isOpen,
  onClose,
  onContinue,
  greetingName,
}: WelcomeBottomSheetProps) {
  const t = useT();

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      stickyHeader={
        <Heading size='lg' className='font-heading-semibold text-primary-800 dark:text-primary-0'>
          {greetingName
            ? t("Hi {greetingName}, let's get started!", { greetingName })
            : t("Let's get started!")}
        </Heading>
      }
      footer={
        <CtaButton onPress={onContinue} testID='welcome-continue'>
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='xl'>
        <View className='h-[200px] w-full items-center justify-center overflow-hidden rounded-vui-sm bg-[#61b0ff]'>
          <WelcomeSsoUserImage width='100%' height='100%' preserveAspectRatio='xMidYMid meet' />
        </View>

        <Text size='md'>
          <T _str='Vida gives you a plan, expert help, and support to reach your health goals and feel your best.' />
        </Text>

        <VStack space='lg'>
          <Text weight='semibold' className='text-typography-900'>
            <T _str='Getting started is easy' />
          </Text>
          <BulletRow>
            <T _str='Confirm your date of birth' />
          </BulletRow>
          <BulletRow>
            <T _str='Answer a few health questions' />
          </BulletRow>
          <BulletRow>
            <T _str='Meet your care team' />
          </BulletRow>
        </VStack>
      </VStack>
    </BottomSheet>
  );
}

function BulletRow({ children }: { children: React.ReactNode }) {
  return (
    <HStack className='items-center gap-3'>
      <CheckIcon width={24} height={24} />
      <Text className='text-typography-700'>{children}</Text>
    </HStack>
  );
}
